import * as React from "react";
import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { PLAN_LIMITS, CURRENT_PLAN } from "../../constants/plan";
import { useFolderList, useCreateFolder, useDeleteFolder } from "../../queries/folders";
import { usePdfList, useDeletePdf, useRenamePdf, useToggleAutoFullscreen } from "../../queries/pdfs";
import type { PdfFileRow } from "../../db/pdfRepository";
import { useTimeAgoLocale, formatTimeAgo } from "../../hooks/useTimeAgo";
import { pdfjs } from "react-pdf";
import SkeletonCard from "../../components/primitives/SkeletonCard";
import SearchInput from "../../components/primitives/SearchInput";
import SortDropdown from "../../components/primitives/SortDropdown";
import { usePdfFilterSort } from "./usePdfFilterSort";
import ConfirmModal from "../../components/primitives/ConfirmModal";
import UploadProgressBar from "../../components/feedback/UploadProgressBar";
import EmptyState from "../../components/feedback/EmptyState";
import StorageMeter from "../../components/layout/StorageMeter";
import FolderColorPicker from "../../components/forms/FolderColorPicker";
import PdfCard from "../../components/pdf/PdfCard";

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FOLDER_COLOR_VALUES = [
  { value: "#6366F1", labelKey: "dashboard.color_indigo" },
  { value: "#8B5CF6", labelKey: "dashboard.color_purple" },
  { value: "#EC4899", labelKey: "dashboard.color_pink" },
  { value: "#EF4444", labelKey: "dashboard.color_red" },
  { value: "#F97316", labelKey: "dashboard.color_orange" },
  { value: "#EAB308", labelKey: "dashboard.color_yellow" },
  { value: "#22C55E", labelKey: "dashboard.color_green" },
  { value: "#14B8A6", labelKey: "dashboard.color_teal" },
] as const;

export default function Dashboard() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 업로드 상태
  const [pdfLoading, setPdfLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // 사이드바 상태
  const queryClient = useQueryClient();
  const { data: folders = [] } = useFolderList(user?.id ?? null);
  const createFolderMutation = useCreateFolder();
  const deleteFolderMutation = useDeleteFolder();
  const { data: pdfList = [], isLoading: pdfListLoading } = usePdfList(user?.id ?? null);
  const deletePdfMutation = useDeletePdf();
  const renamePdfMutation = useRenamePdf();
  const autoFullscreenMutation = useToggleAutoFullscreen();
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectColor, setNewProjectColor] = useState("#6366F1");
  const usedStorage = useMemo(
    () => Number((pdfList.reduce((sum, pdf) => sum + ((pdf as any).file_size || 0), 0) / (1024 * 1024)).toFixed(1)),
    [pdfList],
  );
  const maxStorage = PLAN_LIMITS[CURRENT_PLAN].maxTotalStorage;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const timeAgoLocale = useTimeAgoLocale();

  const pdfListLoaded = !pdfListLoading;

  // 검색·정렬 — 훅으로 분리
  const {
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    sortOptions,
    filteredAndSorted: filteredAndSortedPdfList,
  } = usePdfFilterSort(pdfList, selectedProject);

  // 파일 이름 변경 상태
  const [editingPdfId, setEditingPdfId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>("");

  // PDF 삭제 확인 모달 상태
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    pdfId: string | null;
    pdfName: string;
  }>({
    isOpen: false,
    pdfId: null,
    pdfName: "",
  });

  /** 폴더 삭제 확인 모달 상태 */
  const [deleteFolderConfirmModal, setDeleteFolderConfirmModal] = useState<{
    isOpen: boolean;
    folderId: string | null;
    folderName: string;
  }>({
    isOpen: false,
    folderId: null,
    folderName: "",
  });

  // 사용자 인증 확인
  const checkUser = async () => {
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (authUser) {
        setUser(authUser);
        setLoading(false);
        return;
      }

      navigate("/login");
    } catch (err) {
      console.error("사용자 확인 오류:", err);
      navigate("/login");
    }
  };

  useEffect(() => {
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 로그아웃
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (err) {
      console.error("로그아웃 오류:", err);
      navigate("/");
    }
  };

  // 자동 재생 설정 변경
  const handleAutoPlayChange = async (pdfId: string, checked: boolean) => {
    if (!user) return;
    await autoFullscreenMutation.mutateAsync({ pdfId, userId: user.id, enabled: checked });
  };

  // PDF 파일 업로드 핸들러
  const handlePdfUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    const fileSizeMB = file.size / (1024 * 1024);
    const maxFileSize = PLAN_LIMITS[CURRENT_PLAN].maxFileSize;
    if (fileSizeMB > maxFileSize) {
      alert(t("dashboard.file_too_large", { limit: maxFileSize }));
      return;
    }
    if (usedStorage + fileSizeMB > maxStorage) {
      alert(t("dashboard.storage_exceeded", { limit: maxStorage }));
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "ppt" || ext === "pptx") {
      await uploadAndConvertPpt(file);
    } else if (file.type === "application/pdf" || ext === "pdf") {
      await uploadPdfToSupabase(file);
    } else {
      alert(t("dashboard.pdf_only"));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file || !user) return;

    const fileSizeMB = file.size / (1024 * 1024);
    const maxFileSize = PLAN_LIMITS[CURRENT_PLAN].maxFileSize;
    if (fileSizeMB > maxFileSize) {
      alert(t("dashboard.file_too_large", { limit: maxFileSize }));
      return;
    }
    if (usedStorage + fileSizeMB > maxStorage) {
      alert(t("dashboard.storage_exceeded", { limit: maxStorage }));
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "ppt" || ext === "pptx") {
      await uploadAndConvertPpt(file);
    } else if (file.type === "application/pdf" || ext === "pdf") {
      await uploadPdfToSupabase(file);
    } else {
      alert(t("dashboard.pdf_only"));
    }
  };

  const uploadAndConvertPpt = async (file: File) => {
    if (!user) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    const timestamp = Date.now();
    const ext = file.name.split(".").pop() || "pptx";
    const storagePath = `${user.id}/${timestamp}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("pdfs")
      .upload(storagePath, file, { cacheControl: "3600", upsert: false });

    if (uploadError) {
      alert(t("dashboard.upload_failed", { message: uploadError.message }));
      return;
    }

    const pdfPath = storagePath.replace(/\.(ppt|pptx)$/i, ".pdf");
    const { data: newRow, error: dbError } = await supabase
      .from("pdf_files")
      .insert({
        user_id: user.id,
        file_name: file.name.replace(/\.(ppt|pptx)$/i, ".pdf"),
        file_path: pdfPath,
        original_path: storagePath,
        file_size: file.size,
        page_count: 0,
        current_page: 1,
        auto_fullscreen: false,
        status: "converting",
      })
      .select()
      .single();

    if (dbError || !newRow) {
      alert(t("dashboard.save_failed", { message: dbError?.message }));
      await supabase.storage.from("pdfs").remove([storagePath]);
      return;
    }

    queryClient.setQueryData<PdfFileRow[]>(["pdfs", user.id], (old) => [newRow as PdfFileRow, ...(old ?? [])]);

    const converterUrl = import.meta.env.VITE_CONVERTER_URL;
    const apiKey = import.meta.env.VITE_CONVERTER_API_KEY;
    fetch(`${converterUrl}/convert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify({ path: storagePath, id: newRow.id, jwt: session.access_token }),
    }).catch((err) => console.error("Converter call failed:", err));

    const channel = supabase
      .channel(`pdf-status-${newRow.id}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "pdf_files",
        filter: `id=eq.${newRow.id}`,
      }, (payload) => {
        const updated = payload.new as PdfFileRow;
        queryClient.setQueryData<PdfFileRow[]>(["pdfs", user.id], (old) =>
          (old ?? []).map((p) => (p.id === newRow.id ? updated : p)),
        );
        if (updated.status === "done" || updated.status === "error") {
          supabase.removeChannel(channel);
        }
      })
      .subscribe();
  };

  // Supabase Storage에 PDF 업로드
  const uploadPdfToSupabase = async (file: File) => {
    if (!user) return;

    setPdfLoading(true);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop() || "pdf";
      const fileName = `${user.id}/${timestamp}.${fileExtension}`;

      const { error: uploadError } = await supabase.storage
        .from("pdfs")
        .upload(fileName, file, { cacheControl: "3600", upsert: false });

      if (uploadError) {
        alert(t("dashboard.upload_failed", { message: uploadError.message }));
        setPdfLoading(false);
        return;
      }

      setUploadProgress(50);

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      setUploadProgress(60);

      // PDF.js로 첫 페이지 썸네일 생성
      let thumbnail_url: string | undefined;
      try {
        const firstPage = await pdf.getPage(1);
        const viewport = firstPage.getViewport({ scale: 0.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;
        await firstPage.render({ canvasContext: ctx, viewport }).promise;

        const thumbnailBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) =>
              blob ? resolve(blob) : reject(new Error("썸네일 변환 실패")),
            "image/jpeg",
            0.8,
          );
        });

        const thumbnailPath = `${user.id}/${timestamp}.jpg`;
        const { error: thumbnailError } = await supabase.storage
          .from("thumbnails")
          .upload(thumbnailPath, thumbnailBlob, {
            contentType: "image/jpeg",
            cacheControl: "86400",
          });

        if (!thumbnailError) {
          const { data: thumbUrlData } = supabase.storage
            .from("thumbnails")
            .getPublicUrl(thumbnailPath);
          thumbnail_url = thumbUrlData.publicUrl;
        }
      } catch (thumbErr) {
        console.warn("썸네일 생성 실패 (무시):", thumbErr);
      }

      setUploadProgress(80);

      const { error: dbError } = await supabase.from("pdf_files").insert({
        user_id: user.id,
        file_name: file.name,
        file_path: fileName,
        file_size: file.size,
        page_count: pageCount,
        current_page: 1,
        auto_fullscreen: false,
        thumbnail_url,
      });

      if (dbError) {
        alert(t("dashboard.save_failed", { message: dbError.message }));
        await supabase.storage.from("pdfs").remove([fileName]);
        setPdfLoading(false);
        return;
      }

      setUploadProgress(100);
      await queryClient.invalidateQueries({ queryKey: ["pdfs"] });

      setTimeout(() => {
        setUploadProgress(0);
        setPdfLoading(false);
      }, 500);
    } catch (err) {
      console.error("업로드 오류:", err);
      alert(t("dashboard.upload_error"));
      setPdfLoading(false);
      setUploadProgress(0);
    }
  };

  // PDF 삭제
  const closePdf = async (pdfId: string) => {
    if (!user) return;

    try {
      const pdf = pdfList.find((p) => p.id === pdfId);
      if (pdf) {
        await deletePdfMutation.mutateAsync({
          pdfId,
          filePath: pdf.file_path,
          thumbnailUrl: pdf.thumbnail_url,
        });
      }

      setDeleteConfirmModal({ isOpen: false, pdfId: null, pdfName: "" });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("PDF 삭제 오류:", err);
      alert(t("dashboard.delete_error"));
    }
  };

  // 삭제 확인 모달 열기
  const openDeleteConfirm = (pdfId: string, pdfName: string) => {
    setDeleteConfirmModal({ isOpen: true, pdfId, pdfName });
  };

  // 파일 이름 변경
  const handleRenameStart = (pdf: any) => {
    setEditingPdfId(pdf.id);
    setEditingFileName(pdf.file_name.replace(".pdf", ""));
  };

  const handleRenameCancel = () => {
    setEditingPdfId(null);
    setEditingFileName("");
  };

  const handleRenameSave = async (pdfId: string) => {
    if (!editingFileName.trim()) {
      alert(t("dashboard.enter_file_name"));
      return;
    }
    try {
      const newFileName = editingFileName.trim() + ".pdf";
      await renamePdfMutation.mutateAsync({ pdfId, newFileName });
      setEditingPdfId(null);
      setEditingFileName("");
    } catch (err) {
      console.error("파일 이름 변경 오류:", err);
      alert(t("dashboard.rename_error"));
    }
  };

  // 폴더(프로젝트) 관리
  const handleAddProject = async () => {
    if (!newProjectName.trim() || !user) return;
    await createFolderMutation.mutateAsync({ userId: user.id, name: newProjectName.trim(), color: newProjectColor });
    setNewProjectName("");
    setNewProjectColor("#6366F1");
    setIsAddingProject(false);
  };

  /**
   * 폴더 삭제 확인 모달을 엽니다.
   * @param projectId - 삭제할 폴더 ID
   */
  const handleDeleteProject = (projectId: string) => {
    const folder = folders.find((f) => f.id === projectId);
    setDeleteFolderConfirmModal({
      isOpen: true,
      folderId: projectId,
      folderName: folder?.name ?? "",
    });
  };

  /** 폴더 삭제 확인 후 실제 삭제를 수행합니다. */
  const confirmDeleteFolder = async () => {
    if (!deleteFolderConfirmModal.folderId) return;
    await deleteFolderMutation.mutateAsync(deleteFolderConfirmModal.folderId);
    if (selectedProject === deleteFolderConfirmModal.folderId)
      setSelectedProject(null);
    setDeleteFolderConfirmModal({
      isOpen: false,
      folderId: null,
      folderName: "",
    });
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return Math.round(bytes / (1024 * 1024)) + " MB";
  };

  return (
    <div className="bg-gray-50 flex min-h-screen">
      {/* PDF 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() =>
          setDeleteConfirmModal({ isOpen: false, pdfId: null, pdfName: "" })
        }
        onConfirm={() =>
          deleteConfirmModal.pdfId && closePdf(deleteConfirmModal.pdfId)
        }
        iconClass="ri-delete-bin-line"
        title={t("dashboard.delete_file_confirm")}
        description={
          <>
            <span className="font-medium text-gray-700">
              {deleteConfirmModal.pdfName}
            </span>
            <br />
            {t("dashboard.delete_file_warning")}
          </>
        }
        cancelLabel={t("common.cancel")}
        confirmLabel={t("common.delete")}
      />

      {/* 폴더 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteFolderConfirmModal.isOpen}
        onClose={() =>
          setDeleteFolderConfirmModal({
            isOpen: false,
            folderId: null,
            folderName: "",
          })
        }
        onConfirm={confirmDeleteFolder}
        iconClass="ri-folder-line"
        title={t("dashboard.delete_folder_confirm")}
        description={
          <>
            {t("dashboard.delete_folder_name", {
              name: deleteFolderConfirmModal.folderName,
            })}
            <br />
            {t("dashboard.delete_folder_warning")}
          </>
        }
        cancelLabel={t("common.cancel")}
        confirmLabel={t("common.delete")}
      />

      {/* 모바일 사이드바 오버레이 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 사이드바 */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* 로고 */}
        <div className="px-4 py-4 h-[73px] flex items-center justify-center">
          <img
            src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
            alt="Qpresen Logo"
            className="h-12 object-contain"
          />
        </div>

        {/* 메뉴 */}
        <div className="flex-1 overflow-y-auto px-3">
          {/* 전체 프레젠테이션 */}
          <button
            onClick={() => setSelectedProject(null)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer mb-3 ${
              selectedProject === null
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <i className="ri-folder-line text-base"></i>
              <span className="font-medium">
                {t("dashboard.all_presentations")}
              </span>
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                selectedProject === null
                  ? "bg-indigo-100 text-indigo-600"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {pdfList.length}
            </span>
          </button>

          {/* 새 프로젝트 버튼 */}
          <button
            onClick={() => setIsAddingProject(true)}
            className="hidden sm:flex w-full items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer shadow-sm mb-4"
          >
            <i className="ri-add-line text-lg"></i>
            {t("dashboard.new_project")}
          </button>

          {/* 프로젝트 섹션 */}
          <div className="mt-2">
            <div className="flex items-center justify-between px-3 mb-2">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t("dashboard.projects")}
              </span>
            </div>
            <div className="space-y-1">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className={`group flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                    selectedProject === folder.id
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setSelectedProject(folder.id)}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: folder.color }}
                    ></div>
                    <span>{folder.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedProject === folder.id
                          ? "bg-indigo-100 text-indigo-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {pdfList.filter((p) => p.folder_id === folder.id).length}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(folder.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}

              {isAddingProject && (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddProject();
                        if (e.key === "Escape") {
                          setIsAddingProject(false);
                          setNewProjectName("");
                        }
                      }}
                      placeholder={t("dashboard.project_name_placeholder")}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      autoFocus
                    />
                    <button
                      onClick={handleAddProject}
                      className="w-7 h-7 flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white rounded-md transition-all cursor-pointer"
                    >
                      <i className="ri-check-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingProject(false);
                        setNewProjectName("");
                        setNewProjectColor("#6366F1");
                      }}
                      className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-md transition-all cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                  <FolderColorPicker
                    colors={FOLDER_COLOR_VALUES.map((c) => ({
                      value: c.value,
                      label: t(c.labelKey),
                    }))}
                    value={newProjectColor}
                    onChange={setNewProjectColor}
                    ariaLabel={t("dashboard.new_folder")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 사이드바 하단: 저장 공간 + 로그아웃 */}
        <div className="px-4 py-4 border-t border-gray-200 space-y-3">
          <StorageMeter
            used={usedStorage}
            max={maxStorage}
            label={t("dashboard.storage")}
          />
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all cursor-pointer"
          >
            <i className="ri-logout-box-r-line"></i>
            {t("common.logout")}
          </button>
        </div>
      </aside>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 min-w-0">
        {/* 상단 헤더 */}
        <header className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-0 flex items-center flex-shrink-0 h-[60px] sm:h-[73px] border-b border-gray-200">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              {/* 모바일 메뉴 버튼 */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>

              {/* 저장 공간 표시 */}
              <StorageMeter used={usedStorage} max={maxStorage} variant="inline" />
            </div>

            {/* 오른쪽: 업로드 버튼 */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap shadow-sm"
            >
              <i className="ri-upload-cloud-2-line text-base"></i>
              <span className="hidden sm:inline">
                {t("dashboard.pdf_upload")}
              </span>
              <span className="sm:hidden">{t("dashboard.upload")}</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,.ppt,.pptx"
              onChange={handlePdfUpload}
              className="hidden"
              disabled={pdfLoading}
            />
          </div>
        </header>

        {/* 업로드 진행률 바 */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <UploadProgressBar percent={uploadProgress} />
        )}

        {/* 콘텐츠 영역 */}
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* 모바일 QR 스캔 버튼 */}
          {isMobile && (
            <div className="mb-6">
              <button
                onClick={() => navigate("/qr-scanner")}
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl text-base font-medium transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-qr-scan-2-line text-2xl"></i>
                {t("dashboard.qr_scan")}
              </button>
            </div>
          )}

          {/* 검색 및 정렬 */}
          <div className="flex items-center gap-3 mb-6">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t("dashboard.search_placeholder")}
            />
            <SortDropdown<typeof sortOption>
              options={sortOptions}
              value={sortOption}
              onChange={setSortOption}
            />
          </div>

          {/* 검색 결과 안내 */}
          {searchQuery && (
            <div className="mb-4 text-sm text-gray-500">
              {t("dashboard.search_result_count", {
                count: filteredAndSortedPdfList.length,
              })}
            </div>
          )}

          {/* PDF 카드 그리드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {/* 업로드 영역 */}
            {!loading && (
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all cursor-pointer w-full flex flex-col items-center justify-center min-h-[280px]"
                onClick={() => !pdfLoading && fileInputRef.current?.click()}
              >
                {pdfLoading ? (
                  <div className="space-y-3">
                    <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                      <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-500 border-t-transparent"></div>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {t("dashboard.uploading")}
                    </h3>
                    {uploadProgress > 0 && (
                      <p className="text-sm text-indigo-600">
                        {uploadProgress}%
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {t("dashboard.pdf_upload")}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {t("dashboard.click_or_drag")}
                    </p>
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-all whitespace-nowrap">
                      <i className="ri-folder-open-line"></i>
                      {t("dashboard.select_file")}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* PDF 카드 목록 */}
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={`skeleton-${i}`} />
              ))}
            {!loading &&
              filteredAndSortedPdfList.map((pdf) => (
                <PdfCard
                  key={pdf.id}
                  pdf={pdf}
                  timeAgoLabel={formatTimeAgo(pdf.created_at, timeAgoLocale)}
                  formattedFileSize={formatFileSize(pdf.file_size || 0)}
                  edit={{
                    isEditing: editingPdfId === pdf.id,
                    value: editingFileName,
                    onStart: () => handleRenameStart(pdf),
                    onChange: setEditingFileName,
                    onSave: () => handleRenameSave(pdf.id),
                    onCancel: handleRenameCancel,
                  }}
                  onAutoPlayChange={(checked) =>
                    handleAutoPlayChange(pdf.id, checked)
                  }
                  onPlay={() =>
                    navigate(
                      isMobile
                        ? `/presentations/${pdf.id}/remote`
                        : `/presentations/${pdf.id}`,
                    )
                  }
                  actions={{
                    onSpeakerNotes: () =>
                      navigate(`/dashboard/speaker-notes?id=${pdf.id}`),
                    onShare: () => navigate(`/dashboard/share?id=${pdf.id}`),
                    onMoveFolder: () =>
                      navigate(`/dashboard/folder?id=${pdf.id}`),
                    onDelete: () => openDeleteConfirm(pdf.id, pdf.file_name),
                  }}
                />
              ))}
          </div>

          {/* 빈 상태 */}
          {!loading &&
            pdfListLoaded &&
            filteredAndSortedPdfList.length === 0 &&
            !pdfLoading && (
              <EmptyState
                iconClass="ri-file-pdf-line"
                title={
                  searchQuery
                    ? t("dashboard.no_search_results")
                    : t("dashboard.no_presentations")
                }
                hint={
                  !searchQuery ? t("dashboard.no_presentations_hint") : undefined
                }
              />
            )}
        </div>
      </main>
    </div>
  );
}
